import { ApiError, apiFetch } from './api';

export const ADMIN_PIN_HEADER = 'X-Admin-Pin';

/** DELETE with the PIN in a header. The PIN is not placed in the URL or the body. */
export function adminPinDelete(pin: string): RequestInit {
  return {
    method: 'DELETE',
    headers: { [ADMIN_PIN_HEADER]: pin },
  };
}

export async function deleteGalleryAlbum(id: string, pin: string): Promise<void> {
  await apiFetch(`/api/gallery/albums/${encodeURIComponent(id)}`, adminPinDelete(pin));
}

export async function deleteGalleryPhoto(id: string, pin: string): Promise<void> {
  await apiFetch(`/api/gallery/photos/${encodeURIComponent(id)}`, adminPinDelete(pin));
}

export async function deletePage(id: string, pin: string): Promise<void> {
  await apiFetch(`/api/pages/${encodeURIComponent(id)}`, adminPinDelete(pin));
}

export async function deleteQuizCampaign(id: string, pin: string): Promise<void> {
  await apiFetch(`/api/admin/quiz-campaigns/${encodeURIComponent(id)}`, adminPinDelete(pin));
}

export async function deleteQuizRound(id: string, pin: string): Promise<void> {
  await apiFetch(`/api/admin/quiz-sessions/${encodeURIComponent(id)}`, adminPinDelete(pin));
}

export interface PinFailure {
  message: string;
  /** Seconds the confirm button stays disabled. Null when the admin can try again. */
  lockSeconds: number | null;
  /** Wrong PINs are cleared so the same value is not submitted again by accident. */
  clearPin: boolean;
}

/** Map a failed PIN request onto the dialog. The server text is not shown raw for the known cases. */
export function pinDeleteError(err: unknown): PinFailure {
  if (err instanceof ApiError) {
    if (err.status === 503) {
      return { message: 'No PIN set up on the server', lockSeconds: null, clearPin: false };
    }
    if (err.status === 429) {
      const seconds = err.retryAfterSeconds && err.retryAfterSeconds > 0 ? err.retryAfterSeconds : 60;
      return {
        message: `Too many wrong PINs. Try again in ${seconds} seconds.`,
        lockSeconds: seconds,
        clearPin: true,
      };
    }
    if (err.status === 403 && (err.code === 'Wrong PIN' || err.message === 'Wrong PIN')) {
      const left = err.attemptsLeft;
      return {
        message: left != null ? `Wrong PIN – ${left} attempts left` : 'Wrong PIN',
        lockSeconds: null,
        clearPin: true,
      };
    }
    return {
      message: err.message ? `${err.message} Try again.` : 'Could not delete this. Try again.',
      lockSeconds: null,
      clearPin: false,
    };
  }
  if (err instanceof TypeError) {
    return { message: 'Could not reach the server. Try again.', lockSeconds: null, clearPin: false };
  }
  return { message: 'Could not delete this. Try again.', lockSeconds: null, clearPin: false };
}
